'use client';

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { changePassword } from '@/shared/services/accountService';
import { useToast } from '@/shared/contexts';

type State = 'form' | 'saving' | 'success' | 'error';

/**
 * Recebe o link de reset de senha do email ({verify_base_url}/password/<key>).
 * O usuario digita a nova senha e chamamos GET /Account/changepassword?key&password.
 * A key vem prefixada com RST_ e expira em 1h (backend account/routes.py).
 */
export default function NovaSenhaPage() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { setOpenVerify } = useToast();

  const keyOk = !!key && key.startsWith('RST_');
  const [state, setState] = useState<State>(keyOk ? 'form' : 'error');
  const [message, setMessage] = useState<string>(keyOk ? '' : 'Link inválido ou incompleto.');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key) return;
    if (pw.length < 6) { setMessage('A senha deve ter no mínimo 6 caracteres.'); return; }
    if (pw !== pw2) { setMessage('As senhas não conferem.'); return; }
    setState('saving'); setMessage('');
    try {
      const res: any = await changePassword(key, pw);
      const st = String(res?.Status ?? res?.status ?? '');
      if (/suc/i.test(st)) {
        setState('success');
        setMessage(res?.Message || res?.message || 'Senha alterada com sucesso.');
      } else {
        setState('error');
        setMessage(res?.Message || res?.message || 'Não foi possível alterar. O link pode ter expirado.');
      }
    } catch (err: any) {
      setState('error');
      setMessage(
        err?.detail?.Message || err?.detail?.message || err?.Message || err?.message ||
        'Link inválido ou expirado (validade de 1 hora). Solicite um novo.',
      );
    }
  };

  const inputCls =
    'w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm text-white outline-none focus:border-[#00E1D7]/60';

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-[#05070C] text-white">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.88) 100%), url('/assets/bg3.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        aria-hidden
      />

      <div className="relative z-10 mx-4 w-full max-w-md rounded-2xl border border-white/10 bg-black/60 p-8 text-center backdrop-blur-xl">
        {(state === 'form' || state === 'saving') && (
          <form onSubmit={submit}>
            <Lock className="mx-auto h-12 w-12 text-[#00E1D7]" />
            <h1 className="mt-4 text-2xl font-extrabold">Criar nova senha</h1>
            <p className="mt-2 text-sm text-white/65">Digite sua nova senha de acesso.</p>
            <div className="mt-6 grid gap-3 text-left">
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'} value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Nova senha" className={inputCls} autoComplete="new-password"
                />
                <button
                  type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <input
                type={show ? 'text' : 'password'} value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Confirmar nova senha" className={inputCls} autoComplete="new-password"
              />
              {message && <p className="text-xs text-[#FA6060]">{message}</p>}
              <Button
                type="submit" disabled={state === 'saving'}
                className="h-12 w-full rounded-xl bg-[#00E1D7] text-base font-extrabold uppercase tracking-wider text-black transition hover:bg-[#00E1D7]/90 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {state === 'saving' ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                Salvar nova senha
              </Button>
            </div>
          </form>
        )}

        {state === 'success' && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#00E1D7]" />
            <h1 className="mt-4 text-2xl font-extrabold">Senha alterada!</h1>
            <p className="mt-2 text-sm text-white/65">{message}</p>
            <div className="mt-6 grid gap-3">
              <Button
                className="h-12 w-full rounded-xl bg-[#00E1D7] text-base font-extrabold uppercase tracking-wider text-black transition hover:bg-[#00E1D7]/90"
                onClick={() => { navigate('/'); setOpenVerify(true); }}
              >
                Fazer login
              </Button>
              <button type="button" className="text-xs text-white/55 hover:text-white/80" onClick={() => navigate('/')}>
                Voltar pra home
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-[#FA0000]" />
            <h1 className="mt-4 text-2xl font-extrabold">Não foi possível alterar</h1>
            <p className="mt-2 text-sm text-white/65">{message}</p>
            <div className="mt-6 grid gap-3">
              <Button
                className="h-12 w-full rounded-xl bg-[#00E1D7] text-base font-extrabold uppercase tracking-wider text-black transition hover:bg-[#00E1D7]/90"
                onClick={() => navigate('/')}
              >
                Voltar pra home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
